import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { naira } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { C } from "./AccountingLayout";

export default function Journals() {
  const { locationId } = useOutletContext();
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const [accs, setAccs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ memo: "", reference: "", location_id: "", lines: [{ account_code: "", debit: 0, credit: 0, description: "" }, { account_code: "", debit: 0, credit: 0, description: "" }] });

  const load = () => {
    const p = locationId ? { location_id: locationId } : {};
    api.get("/journals", { params: p }).then(setRows).catch(() => {});
  };
  useEffect(() => { load(); api.get("/accounts").then(setAccs).catch(() => {}); /* eslint-disable-next-line */ }, [locationId]);

  const totalD = draft.lines.reduce((s, l) => s + Number(l.debit || 0), 0);
  const totalC = draft.lines.reduce((s, l) => s + Number(l.credit || 0), 0);
  const balanced = Math.abs(totalD - totalC) < 0.01 && totalD > 0;

  const post = async () => {
    if (!balanced) return toast.error(`Not balanced: Dr ${totalD.toFixed(2)} vs Cr ${totalC.toFixed(2)}`);
    setSaving(true);
    try {
      await api.post("/journals", draft);
      toast.success("Journal posted"); setOpen(false);
      setDraft({ memo: "", reference: "", location_id: "", lines: [{ account_code: "", debit: 0, credit: 0, description: "" }, { account_code: "", debit: 0, credit: 0, description: "" }] });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const reverse = async (j) => {
    if (!confirm(`Reverse ${j.number}? This will create a mirror journal.`)) return;
    try { await api.post(`/journals/${j.id}/reverse`); toast.success("Reversed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="journals-page">
      <div className="flex justify-end">
        <Button onClick={() => setOpen(true)} style={{ background: C.burgundy }} className="text-white" data-testid="new-journal-btn"><Plus size={15} className="mr-1" /> New Journal</Button>
      </div>
      <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: C.border }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}>
                <th className="py-3 px-4">Number</th><th className="py-3 px-3">Date</th><th className="py-3 px-3">Memo</th>
                <th className="py-3 px-3">Source</th><th className="py-3 px-3 text-right">Debit</th><th className="py-3 px-3 text-right">Credit</th>
                <th className="py-3 px-3">Status</th><th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j, i) => (
                <tr key={i} className="border-b hover:bg-[#FAF6EC] cursor-pointer" style={{ borderColor: C.borderSoft }} onClick={() => setView(j)}>
                  <td className="py-2.5 px-4 font-semibold" style={{ color: C.burgundy }}>{j.number}</td>
                  <td className="py-2.5 px-3 text-[12px]" style={{ color: C.muted }}>{(j.date || "").slice(0, 10)}</td>
                  <td className="py-2.5 px-3 text-[12.5px]" style={{ color: C.text }}>{j.memo}</td>
                  <td className="py-2.5 px-3 text-[11px] uppercase font-semibold" style={{ color: C.softMuted }}>{j.source}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-brand font-bold" style={{ color: C.text }}>{naira(j.total_debit)}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums font-brand font-bold" style={{ color: C.text }}>{naira(j.total_credit)}</td>
                  <td className="py-2.5 px-3"><span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: j.reversed_by ? C.redTint : C.greenTint, color: j.reversed_by ? C.red : C.green }}>{j.reversed_by ? "Reversed" : "Posted"}</span></td>
                  <td className="py-2.5 px-4">{!j.reversed_by && j.source === "manual" && <button onClick={(e) => { e.stopPropagation(); reverse(j); }} className="text-[12px] font-semibold flex items-center gap-1" style={{ color: C.red }}><Undo2 size={11} /> Reverse</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-brand">Journal {view?.number}</DialogTitle></DialogHeader>
          <div className="text-[13px] space-y-1 mb-2">
            <div><span style={{ color: C.muted }}>Date:</span> <span style={{ color: C.text }}>{(view?.date || "").slice(0, 10)}</span></div>
            <div><span style={{ color: C.muted }}>Memo:</span> <span style={{ color: C.text }}>{view?.memo}</span></div>
            <div><span style={{ color: C.muted }}>Source:</span> <span style={{ color: C.text }}>{view?.source} · {view?.reference}</span></div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-[10px] uppercase" style={{ color: C.softMuted, borderColor: C.borderSoft }}><th className="text-left py-2">Account</th><th className="text-left py-2">Description</th><th className="text-right py-2">Debit</th><th className="text-right py-2">Credit</th></tr></thead>
            <tbody>{view?.lines?.map((l, i) => (
              <tr key={i} className="border-b" style={{ borderColor: C.borderSoft }}>
                <td className="py-2"><span className="font-mono text-[11px]" style={{ color: C.softMuted }}>{l.account_code}</span> {l.account_name}</td>
                <td className="py-2 text-[12.5px]" style={{ color: C.text }}>{l.description}</td>
                <td className="py-2 text-right tabular-nums">{l.debit > 0 ? naira(l.debit) : "—"}</td>
                <td className="py-2 text-right tabular-nums">{l.credit > 0 ? naira(l.credit) : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-brand">New Journal Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 mb-2">
            <div className="space-y-1"><Label>Memo</Label><Input value={draft.memo} onChange={(e) => setDraft({ ...draft, memo: e.target.value })} data-testid="j-memo" /></div>
            <div className="space-y-1"><Label>Reference</Label><Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} data-testid="j-ref" /></div>
          </div>
          <table className="w-full text-sm mt-2">
            <thead><tr className="text-[10px] uppercase" style={{ color: C.softMuted }}><th className="text-left py-2">Account</th><th className="text-left py-2">Description</th><th className="text-right py-2 w-32">Debit</th><th className="text-right py-2 w-32">Credit</th></tr></thead>
            <tbody>{draft.lines.map((l, i) => (
              <tr key={i}>
                <td className="py-1 pr-2">
                  <Select value={l.account_code} onValueChange={(v) => { const ls = [...draft.lines]; ls[i].account_code = v; setDraft({ ...draft, lines: ls }); }}>
                    <SelectTrigger className="h-9" data-testid={`j-acc-${i}`}><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>{accs.filter((a) => a.active !== false).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="py-1 pr-2"><Input value={l.description} onChange={(e) => { const ls = [...draft.lines]; ls[i].description = e.target.value; setDraft({ ...draft, lines: ls }); }} className="h-9" /></td>
                <td className="py-1 pr-2"><Input type="number" value={l.debit} onChange={(e) => { const ls = [...draft.lines]; ls[i].debit = e.target.value; setDraft({ ...draft, lines: ls }); }} className="h-9 text-right" /></td>
                <td className="py-1"><Input type="number" value={l.credit} onChange={(e) => { const ls = [...draft.lines]; ls[i].credit = e.target.value; setDraft({ ...draft, lines: ls }); }} className="h-9 text-right" /></td>
              </tr>
            ))}</tbody>
            <tfoot><tr className="font-bold border-t" style={{ borderColor: C.borderSoft }}><td colSpan={2} className="py-2">Total</td><td className="py-2 text-right tabular-nums">{naira(totalD)}</td><td className="py-2 text-right tabular-nums">{naira(totalC)}</td></tr></tfoot>
          </table>
          <button onClick={() => setDraft({ ...draft, lines: [...draft.lines, { account_code: "", debit: 0, credit: 0, description: "" }] })} className="text-[12px] font-semibold self-start" style={{ color: C.gold }} data-testid="j-add-line">+ Add line</button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={post} disabled={saving || !balanced} className="text-white" style={{ background: balanced ? C.burgundy : "#B9AA95" }} data-testid="j-post-btn">{saving ? "Posting…" : balanced ? "Post Journal" : `Not balanced (Dr ${totalD.toFixed(2)} vs Cr ${totalC.toFixed(2)})`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
